import { useQuery } from '@tanstack/react-query'
import { BarChart, Bar, BarXAxis, Grid, ChartTooltip } from '@/components/charts'
import { useAppStore } from '@/store'
import { fetchEuComparison, type CountryComparison } from '@/data/eurostat'
import { useState, useMemo, useEffect } from 'react'

const METRICS = [
  { key: 'totalTaxesPctGdp' as const, label: 'Prélèvements obligatoires', unit: '% du PIB' },
  { key: 'deficitPctGdp' as const, label: 'Déficit public', unit: '% du PIB' },
  { key: 'debtPctGdp' as const, label: 'Dette publique', unit: '% du PIB' },
]

const COLORS: Record<string, string> = {
  FR: '#e94560', DE: '#0984e3', IT: '#00b894', ES: '#6c5ce7',
  NL: '#ff6b35', BE: '#f7c948', AT: '#00cec9', PL: '#e17055',
  PT: '#fd79a8', SE: '#a29bfe', DK: '#fdcb6e', FI: '#74b9ff',
}

const COUNTRY_CODES = Object.keys(COLORS)

export default function ComparaisonsUE() {
  const { selectedYear } = useAppStore()
  const [metricIdx, setMetricIdx] = useState(0)
  const metric = METRICS[metricIdx]

  const query = useQuery({
    queryKey: ['eu-comparison', selectedYear],
    queryFn: () => fetchEuComparison(selectedYear),
    staleTime: 1000 * 60 * 60,
  })

  const countries = query.data ?? []

  const getVal = (c: CountryComparison): number | null => c[metric!.key]

  const sorted = countries
    .filter((c) => getVal(c) != null)
    .sort((a, b) => (getVal(b) ?? 0) - (getVal(a) ?? 0))

  const isDeficit = metric!.key === 'deficitPctGdp'
  const [animated, setAnimated] = useState(false)

  // Reset animation when metric or year changes
  useEffect(() => {
    setAnimated(false)
    const id = setTimeout(() => setAnimated(true), 50)
    return () => clearTimeout(id)
  }, [metricIdx, selectedYear])

  // Bklit bar data (for positive-only metrics)
  const barData = useMemo(() => sorted.map((c) => {
    const row: Record<string, unknown> = { pays: c.label }
    for (const code of COUNTRY_CODES) {
      row[code] = c.code === code ? getVal(c)! : 0
    }
    return row
  }), [sorted, getVal])

  // Diverging bar data (for deficit — can be negative)
  const values = sorted.map(c => getVal(c)!)
  const maxAbs = Math.max(...values.map(Math.abs), 1)
  const minVal = Math.min(...values, 0)
  const zeroPct = (maxAbs / (maxAbs + Math.abs(minVal))) * 100
  const formatVal = (v: number) => `${v >= 0 ? '+' : ''}${v.toFixed(1)}%`

  return (
    <div>
      <div className="mb-6">
        <h2 className="text-2xl font-bold">Comparaisons européennes — {selectedYear}</h2>
        <p className="text-text-muted mt-1">
          France vs principaux pays de l'UE (administrations publiques, S13)
        </p>
      </div>

      {/* Metric selector */}
      <div className="flex gap-2 mb-6">
        {METRICS.map((m, i) => (
          <button
            key={m.key}
            onClick={() => setMetricIdx(i)}
            className={`px-4 py-2 rounded-lg text-sm font-medium transition-colors ${
              metricIdx === i
                ? 'bg-nav text-white'
                : 'bg-card text-text hover:bg-muted'
            }`}
          >
            {m.label}
          </button>
        ))}
      </div>

      {query.isLoading && (
        <div className="bg-card rounded-lg shadow p-8 text-center text-text-muted">
          Chargement des données Eurostat pour 12 pays…
        </div>
      )}

      {sorted.length > 0 && (
        <>
          <div className="bg-card rounded-lg shadow p-6">
            <h3 className="font-semibold text-lg mb-4">
              {metric!.label} ({metric!.unit})
            </h3>

            {isDeficit ? (
              /* Diverging bar chart for deficit (handles negative values) */
              <div className="relative" style={{ minWidth: 420 }}>
                <div
                  className="absolute top-0 bottom-8 w-px bg-border z-10"
                  style={{ left: `${zeroPct}%` }}
                  aria-hidden="true"
                />
                <div className="space-y-1.5">
                  {sorted.map((c) => {
                    const val = getVal(c)!
                    const color = COLORS[c.code] ?? '#94a3b8'
                    const barW = Math.abs(val) / maxAbs * zeroPct

                    return (
                      <div key={c.code} className="flex items-center gap-2 h-7">
                        <div className="w-24 text-right text-xs font-medium text-text flex-shrink-0">
                          {c.label}
                        </div>
                        <div className="relative flex-1 h-5">
                          <div className="absolute inset-0 bg-muted rounded-sm" />
                          <div
                            className="absolute top-0 h-full rounded-sm ease-out"
                            style={{
                              width: animated ? `${barW}%` : '0%',
                              backgroundColor: color,
                              left: val >= 0 ? `${zeroPct}%` : undefined,
                              right: val < 0 ? `${100 - zeroPct}%` : undefined,
                              transition: animated ? 'width 0.7s ease-out' : 'none',
                            }}
                            title={`${c.label}: ${formatVal(val)}`}
                          />
                        </div>
                        <div className="w-16 text-right text-xs font-semibold flex-shrink-0">
                          {formatVal(val)}
                        </div>
                      </div>
                    )
                  })}
                </div>
              </div>
            ) : (
              /* Bklit bar chart for positive-only metrics */
              <BarChart
                data={barData}
                xDataKey="pays"
                orientation="horizontal"
                stacked
                aspectRatio="2 / 1.2"
              >
                <Grid vertical />
                {COUNTRY_CODES.map((code) => (
                  <Bar key={code} dataKey={code} fill={COLORS[code]} />
                ))}
                <BarXAxis showAllLabels />
                <ChartTooltip />
              </BarChart>
            )}
          </div>

          {/* Data table */}
          <div className="bg-card rounded-lg shadow p-6 mt-6">
            <h3 className="font-semibold text-lg mb-4">Tableau comparatif</h3>
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead>
                  <tr className="border-b text-left">
                    <th className="pb-2 pr-4">Pays</th>
                    {METRICS.map((m) => (
                      <th key={m.key} className="pb-2 text-right px-2">
                        {m.label}
                      </th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {countries.map((c) => (
                    <tr key={c.code} className="border-b border-border">
                      <td className="py-2 pr-4 flex items-center gap-2">
                        <span
                          className="w-3 h-3 rounded-full inline-block flex-shrink-0"
                          style={{ backgroundColor: COLORS[c.code] ?? '#94a3b8' }}
                        />
                        <span className={c.code === 'FR' ? 'font-bold' : ''}>{c.label}</span>
                      </td>
                      {METRICS.map((m) => (
                        <td
                          key={m.key}
                          className={`py-2 text-right px-2 ${
                            c.code === 'FR' ? 'font-bold' : ''
                          }`}
                        >
                          {c[m.key] != null ? `${c[m.key]!.toFixed(1)}%` : '—'}
                        </td>
                      ))}
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        </>
      )}

      <p className="text-xs text-text-muted mt-4">
        Sources : Eurostat gov_10dd_edpt1 (déficit, dette) + gov_10a_taxag (prélèvements) — % du PIB, S13
      </p>
    </div>
  )
}
